import {
  mdiLightbulb,
  mdiViewDashboard,
  mdiChartLineVariant,
  mdiAccountMultiple,
  mdiFileChartOutline,
  mdiCameraIris,
  mdiCurrencyUsdCircleOutline,
  mdiAccountDetailsOutline,
  mdiBullhornOutline,
  mdiCashUsdOutline,
  mdiAccountReactivateOutline,
  mdiAccountSearch,
  mdiViewListOutline,
  mdiPlus,
  mdiLinkPlus,
  mdiAnchor,
  mdiPercent,
  mdiNoteText,
  mdiCart,
  mdiTimelineClock,
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useMemo, useState, useEffect } from 'react';
import P from 'router/paths';
import { useSidebar } from 'store/redux/states/ui/ui.hooks';
import { ConfigProvider, Menu } from 'antd';
import Ninja from '@ninja';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import useWorkspace from 'store/redux/hooks/useWorkspace';
import useCrmForms from 'modules/CRMModule/hooks/useCrmForms';
import useEmailConfirm from 'modules/setup/hooks/useEmailConfirm';
import useNetworkAccounts from 'store/redux/hooks/useNetworkAccounts';
import useWebsites from 'modules/website/hooks/useWebsites';
import { useIntegrationListQuery } from 'modules/apps/api/apps.api';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';

const SidebarMenu = () => {
  const [sidebarOpen] = useSidebar();
  const { workspace } = useWorkspace();
  const { websites } = useWebsites();
  const { isConfirmed } = useEmailConfirm();
  const { accounts } = useNetworkAccounts({});
  const { data = [] } = useIntegrationListQuery();
  const { queryParams } = useWidgetDate('global');

  const isSetupDone =
    websites.length &&
    workspace?.setup_finished &&
    accounts.length &&
    isConfirmed &&
    data?.data?.length;

  const { forms } = useCrmForms();

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const items = useMemo(() => {
    const items = [
      !isSetupDone && {
        key: P.SETUP.INDEX,
        label: <Link to={`${P.SETUP.INDEX}${queryParams}`}>Getting Started</Link>,
        icon: <Icon path={mdiLightbulb} />,
        className: 'getting-started-item',
      },
      // {
      //   className: 'ninja-ai-item',
      //   key: P.NINJA_AI.INDEX,
      //   icon: <img src='/icons/ai/ai-gradient.svg' className='w-6 h-6' />,
      //   label: <Link to={`${P.NINJA_AI.INDEX}${queryParams}`}>Generate</Link>,
      // },
      {
        type: 'group',
        key: 'statistics',
        label: 'Statistics',
        children: [
          {
            key: P.DASHBOARD.INDEX,
            label: <Link to={`${P.DASHBOARD.INDEX}${queryParams}`}>Dashboard</Link>,
            icon: <Icon path={mdiViewDashboard} />,
          },
          // {
          //   key: P.SALES.INDEX,
          //   label: <Link to={`${P.SALES.CRM}`}>Sales</Link>,
          //   icon: <Icon path={mdiPercent} />,
          // },
          {
            key: P.RETENTION.CHARTS,
            label: <Link to={`${P.RETENTION.CHARTS}${queryParams}`}>Retention</Link>,
            icon: <Icon path={mdiAccountMultiple} />,
          },
          {
            key: P.ACQUISITION.INDEX,
            label: <Link to={`${P.ACQUISITION.INDEX}${queryParams}`}>Acquisition</Link>,
            icon: <Icon path={mdiChartLineVariant} />,
          },
          {
            key: P.REPORT.INDEX,
            label: <Link to={`${P.REPORT.INDEX}${queryParams}`}>User Journey</Link>,
            icon: <Icon path={mdiTimelineClock} />,
          },
          {
            key: P.REPORTING.INDEX,
            label: <Link to={`${P.REPORTING.INDEX}${queryParams}`}>Reporting</Link>,
            icon: <Icon path={mdiFileChartOutline} />,
          },
          // {
          //   key: P.REPORTING.CREATIVE,
          //   label: 'Creative Analysis',
          //   icon: <Icon path={mdiCameraIris} />,
          //   children: [
          //     {
          //       key: P.CRM.CONTACTS,
          //       label: <Link to={`${P.REPORTING.CREATIVE}?network=meta`}> Creative Analytics</Link>,
          //       icon: <img src='/icons/networks/meta.svg' alt='Meta' className='w-5 h-5' />,
          //     },
          //     {
          //       key: P.CRM.DEALS,
          //       label: (
          //         <Link to={`${P.REPORTING.CREATIVE}?network=tiktok`}>Creative Analytics</Link>
          //       ),
          //       icon: <img src='/icons/networks/tiktok.svg' alt='TikTok' className='w-5 h-5' />,
          //     },
          //   ],
          // },
          {
            key: P.PNL_REPORT.INDEX,
            label: <Link to={`${P.PNL_REPORT.INDEX}${queryParams}`}>P&L Report</Link>,
            icon: <Icon path={mdiNoteText} />,
          },
          {
            key: P.PRODUCTS.INDEX,
            label: <Link to={`${P.PRODUCTS.INDEX}${queryParams}`}>Products</Link>,
            icon: <Icon path={mdiCart} />,
          },
        ],
      },
      {
        type: 'group',
        key: 'crm',
        label: 'CRM',
        children: [
          {
            key: P.CRM.CONTACTS,
            label: <Link to={`${P.CRM.CONTACTS}${queryParams}`}>Contacts</Link>,
            icon: <Icon path={mdiViewListOutline} />,
          },
          {
            key: P.CRM.DEALS,
            label: <Link to={`${P.CRM.DEALS}${queryParams}`}>Deals</Link>,
            icon: <Icon path={mdiCurrencyUsdCircleOutline} />,
          },
          {
            key: 'leads',
            label: 'Leads Forms',
            icon: <Icon path={mdiAccountDetailsOutline} />,
            children: [
              ...((forms?.length &&
                forms?.map((form) => ({
                  label: (
                    <Link
                      style={{ color: 'unset' }}
                      to={`${P.CRM.DATA_LIST.replace(':id', form.id)}${queryParams}`}
                    >
                      {form.name} - {form.leads_count} Leads
                    </Link>
                  ),
                  icon: <Icon path={mdiAccountSearch} />,
                  key: form.id + ':list',
                }))) ||
                []),
              {
                label: (
                  <Link style={{ color: 'unset' }} to={`${P.CRM.INDEX}${queryParams}`}>
                    Create New Form
                  </Link>
                ),
                key: 'create_form',
                icon: <Icon path={mdiPlus} />,
              },
            ],
          },
        ],
      },
      {
        type: 'group',
        key: 'setup',
        label: 'Setup',
        children: [
          {
            key: P.SETUP.MARKETING,
            label: <Link to={P.SETUP.MARKETING}>Marketing Channels</Link>,
            icon: <Icon path={mdiBullhornOutline} />,
          },
          {
            key: P.SETUP.REVENUE,
            label: <Link to={P.SETUP.REVENUE}>Revenue Sources</Link>,
            icon: <Icon path={mdiCashUsdOutline} />,
          },
          {
            key: P.WEBSITES.INDEX,
            label: <Link to={P.WEBSITES.INDEX}>Tracking</Link>,
            icon: <Icon path={mdiAccountReactivateOutline} />,
          },
          {
            key: P.POSTBACKS.INDEX,
            label: <Link to={`${P.POSTBACKS.INDEX}${queryParams}`}>Postbacks</Link>,
            icon: <Icon path={mdiAnchor} />,
          },
          {
            key: P.URL.INDEX,
            label: <Link to={`${P.URL.INDEX}`}>Custom URLs</Link>,
            icon: <Icon path={mdiLinkPlus} />,
          },
        ],
      },
    ];
    return items;
  }, [workspace, forms, isSetupDone, queryParams]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            darkItemBg: Ninja.colors.bgColorDark,
            darkItemHoverBg: Ninja.colors.orange,
            darkItemColor: 'white',
            iconSize: '20px',
            darkPopupBg: '#151a22',
          },
        },
      }}
    >
      <Menu
        className='w-full'
        inlineCollapsed={!sidebarOpen}
        defaultSelectedKeys={['1']}
        defaultOpenKeys={['sub1']}
        mode={isMobile ? 'inline' : 'vertical'}
        theme='dark'
        selectedKeys={[!isSetupDone ? P.SETUP.INDEX : null, window.location.pathname]}
        items={items}
      />
    </ConfigProvider>
  );
};

export default SidebarMenu;
